import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { FreelancerAsyncPipeModule } from '@freelancer/pipes';
import {
  FreelancerBreakpoints,
  FreelancerBreakpointValues,
} from '@freelancer/ui/breakpoints';
import { IconColor, IconModule, IconSize } from '@freelancer/ui/icon';
import { ScrollableContentComponent } from '@freelancer/ui/scrollable-content';
import type { Observable } from 'rxjs';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SidebarItemComponent } from './sidebar-item.component';
import { Sidebar } from './sidebar.service';
import { ViewHeaderComponent } from '../view-header/view-header.component';

@Component({
  standalone: true,
  selector: 'fl-sidebar',
  imports: [
    FreelancerAsyncPipeModule,
    IconModule,
    NgTemplateOutlet,
    ScrollableContentComponent,
    SidebarItemComponent,
    ViewHeaderComponent,
  ],
  template: `
    <div
      class="SidebarOverlay tablet:show"
      [class.hide]="(isSidebarOpen$ | flAsync) === false"
      (click)="toggleSidebar()"
    ></div>
    <div
      #container
      class="SidebarContainer"
      [attr.data-is-open]="isSidebarOpen$ | flAsync"
    >
      <div
        class="SidebarInner"
        [attr.data-is-open]="isSidebarOpen$ | flAsync"
      >
        <!-- view header for mobile only -->
        <fl-view-header
          class="ViewHeader mobile:show"
          [showBackButton]="false"
        >
          <div class="ViewHeader-inner">
            <div class="ViewHeader-title mr-small">
              <h3
                class="text-color-inherit font-bold truncate {{
                  subheading ? 'mb-xxxsmall' : 'mb-none'
                }}"
                [class.text-body-16]="subheading"
                [class.text-body-24]="!subheading"
              >
                @if (heading) {
                  <ng-container [ngTemplateOutlet]="heading"></ng-container>
                } @else {
                  <ng-container i18n="Sidebar default heading"
                    >Menu</ng-container
                  >
                }
              </h3>
              @if (subheading) {
                <h4
                  class="text-color-inherit text-body-16 font-normal truncate"
                >
                  <ng-container [ngTemplateOutlet]="subheading" />
                </h4>
              }
            </div>
            <fl-icon
              class="ViewHeader-closeBtn mobile:show"
              [name]="'ui-close'"
              (click)="toggleSidebar()"
              [size]="IconSize.LARGE"
              [color]="IconColor.INHERIT"
              [useIconFont]="true"
            ></fl-icon>
          </div>
        </fl-view-header>

        <div class="SidebarContent">
          <div class="SidebarContentInner">
            <fl-scrollable-content
              class="SidebarScroll"
              [hideHorizontalScroll]="true"
            >
              <!-- heading for tablet and up -->
              <h3
                class="text-color-inherit text-body-16 SidebarContentTitle font-bold mb-xsmall mobile:hide"
              >
                @if (heading) {
                  <ng-container [ngTemplateOutlet]="heading"></ng-container>
                } @else {
                  <ng-container i18n="Sidebar default heading"
                    >Menu</ng-container
                  >
                }
              </h3>
              <fl-sidebar-item
                class="SidebarTitlePlaceholder"
                [iconName]="'ui-bars-v2'"
                [iconFill]="true"
              ></fl-sidebar-item>
              <ng-content></ng-content>
            </fl-scrollable-content>
          </div>
          <div class="SidebarFooter">
            <ng-content select="fl-sidebar-footer"></ng-content>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnDestroy, OnInit {
  IconColor = IconColor;
  IconSize = IconSize;
  private breakpointObserver = inject(BreakpointObserver);
  private router = inject(Router);
  private sidebar = inject(Sidebar);
  private platformId = inject(PLATFORM_ID);

  @Input() heading?: TemplateRef<any>;
  @Input() subheading?: TemplateRef<any>;

  isSidebarOpen$: Observable<boolean>;
  private subscriptions = new Subscription();

  @ViewChild('container', { read: ElementRef }) container: ElementRef;

  private get isDesktopSmall(): boolean {
    return (
      window.innerWidth >=
        parseInt(FreelancerBreakpointValues.DESKTOP_SMALL, 10) &&
      window.innerWidth <=
        parseInt(FreelancerBreakpointValues.DESKTOP_LARGE, 10)
    );
  }

  @HostListener('mouseenter')
  handleMouseEnter(): void {
    if (this.isDesktopSmall) {
      this.sidebar.toggle(true);
    }
  }

  @HostListener('mouseleave')
  handleMouseLeave(): void {
    if (this.isDesktopSmall) {
      this.sidebar.toggle(false);
    }
  }

  ngOnInit(): void {
    this.isSidebarOpen$ = this.sidebar.isOpen();

    this.subscriptions.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationStart))
        .subscribe(() => {
          this.sidebar.toggle(false);
        }),
    );

    if (isPlatformBrowser(this.platformId)) {
      this.subscriptions.add(
        this.breakpointObserver
          .observe(FreelancerBreakpoints.DESKTOP_LARGE)
          .subscribe(isDesktopLarge => {
            this.sidebar.toggle(isDesktopLarge.matches);
          }),
      );
    }
  }

  toggleSidebar(): void {
    this.sidebar.toggle();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
